<template>
  <main>
    <div>
      <!-- HTML Section -->
      <div>
        <h3 v-if="user">{{ username }} / Funnel / </h3>

        <p>Select HTML Below</p>
        <select v-model="selectedHtml" @change="updateContent('HTML')">
          <option v-for="option in htmlOptions" :key="option" :value="option">{{ option }}</option>
        </select>

        <!-- HTML Buttons -->
        <div class="button-container">
          <button @click="saveContent('HTML')" :disabled="!canEditSnippet('HTML')" :class="{
            'button-disabled': !canEditSnippet('HTML'),
            'save-button': canEditSnippet('HTML'),
            'unchanged': canEditSnippet('HTML') && !htmlIsChanged,
            'changed': canEditSnippet('HTML') && htmlIsChanged
          }">
            <span v-if="!htmlIsChanged">Saved</span>
            <span v-else>Save</span>
          </button>
          <button :disabled="true" @click="showNewPopup('HTML')">New</button>
          <button :disabled="!user" @click="showEditDetailsPopup('HTML')">Edit Details</button>
          <button @click="copyHtml">Copy HTML</button>
          <button @click="copyAllCode">Copy HTML & CSS</button>
          <button :disabled="!user">Share HTML</button>
        </div>
      </div>

      <!-- HTML Code Input -->
      <CodeInput v-model="htmlCode" label="HTML Code" />

      <!-- CSS Section -->
      <div>
        <p>Select CSS Below</p>
        <select v-model="selectedCss" @change="updateContent('CSS')">
          <option v-for="option in cssOptions" :key="option" :value="option">{{ option }}</option>
        </select>
      </div>
      <div v-show="selectedCss !== 'None'">
        <!-- CSS Buttons -->
        <div class="button-container">
          <button @click="saveContent('CSS')" :disabled="!user" :class="{
            'button-disabled': !user,
            'save-button': user,
            'unchanged': user && !cssIsChanged,
            'changed': user && cssIsChanged
          }">
            <span v-if="!cssIsChanged">Saved</span>
            <span v-else>Save</span>
          </button>
          <button :disabled="true" @click="showNewPopup('CSS')">New</button>
          <button :disabled="!user" @click="showEditDetailsPopup('CSS')">Edit Details</button>
          <button @click="copyCss">Copy CSS</button>
        </div>

        <!-- CSS Code Input -->
        <CodeInput v-model="cssCode" label="CSS Code" />
      </div>

      <!-- Code Runner Component -->
      <CodeRunner v-model:showRunPopup="showRunPopup" :htmlCode="htmlCode" :cssCode="cssCode" />
      <!-- <newPopup v-if="showNewPopup" :type="popupType" @close="showNewPopup = false" @documentCreated="handleDocumentCreated" /> -->
      <EditDetails v-if="showEditPopup" :type="popupType" :selectedCssDocId="selectedCssDocId" :selectedHtmlDocId="selectedHtmlDocId" @close="showEditPopup = false" />
    </div>
  </main>
</template>

<script>
import CodeInput from "@/components/CodeInput.vue"; // Code Input Component
import CodeRunner from "@/components/CodeRunner.vue"; // Code Runner Component
import EditDetails from "@/components/editDetails.vue"; // Edit Details Component
import { doc, updateDoc } from "firebase/firestore"; // Firestore Methods
import { db } from "@/firebaseConfig"; // Firestore Collections
import { useAuthStore } from "@/stores/authStore"; // Auth State
import { computed, ref, onMounted, watch } from "vue"; // Vue Composition API
import { useDbStore } from "@/stores/dbStore";

export default {
  name: "FunnelDemo",

  components: {
    CodeRunner,
    CodeInput,
    EditDetails,
  },

  setup() {
    const authStore = useAuthStore();
    const user = computed(() => authStore.user);
    const username = computed(() => authStore.username);
    const dbStore = useDbStore();

    const cssCode = ref('');
    const htmlCode = ref('');
    const cssOptions = ref([]);
    const htmlOptions = ref([]);
    const selectedCss = ref('Slate Blue');
    const selectedHtml = ref('Funnel Report');
    const selectedCssDocId = ref('');
    const selectedHtmlDocId = ref('');
    const showNewPopup = ref(false);
    const showEditPopup = ref(false);
    const showRunPopup = ref(false);
    const popupType = ref('');
    const originalCss = ref('');
    const originalHtml = ref('');

    const htmlIsChanged = computed(() => htmlCode.value !== originalHtml.value);
    const cssIsChanged = computed(() => cssCode.value !== originalCss.value);

    const loadData = () => {
      // Reset option lists to avoid duplicates when snippets are reloaded
      cssOptions.value = [];
      htmlOptions.value = [];

      // Process snippets from dbStore instead of querying Firestore directly
      dbStore.snippets.forEach((data) => {
        if (data.type === "CSS") {
          cssOptions.value.push(data.name);
          if (data.name === "Slate Blue") {
            cssCode.value = data.content; // Set default CSS content
            selectedCssDocId.value = data.id; // Store the Firestore docID
            originalCss.value = data.content; // Sets Value to check for changes
          }
        } else if (data.type === "HTML") {
          htmlOptions.value.push(data.name);
          if (data.name === "Funnel Report") {
            htmlCode.value = data.content; // Set default HTML content
            selectedHtmlDocId.value = data.id; // Store the Firestore docID
            originalHtml.value = data.content; // Sets Value to check for changes
          }
        }
      });

      // Ensure "Default" is selected on load
      selectedCss.value = "Slate Blue";
      selectedHtml.value = "Funnel Report";
    };

    onMounted(async () => {
      if (dbStore.shouldFetchSnippets) {
        await dbStore.fetchSnippets();
      }
    });

    watch(
      () => authStore.user, // Watch for changes in the authenticated user
      async (newUser) => {
        if (newUser && newUser.active && !dbStore.snippets.length) {
          await dbStore.fetchSnippets();
        }
      },
      { immediate: true } // Run this immediately when component loads
    );

    watch(
      () => dbStore.snippets, // Watch for changes in the snippets
      (newSnippets) => {
        if (newSnippets.length) {
          loadData();
        }
      },
      { immediate: true }
    );

    const showEditDetailsPopup = (type) => {
      popupType.value = type;
      showEditPopup.value = true;
    };

    const copyCss = () => {
      navigator.clipboard.writeText(cssCode.value).then(() => {
        alert("CSS code copied to clipboard!");
      });
    };

    const copyHtml = () => {
      navigator.clipboard.writeText(htmlCode.value).then(() => {
        alert("HTML code copied to clipboard!");
      });
    };

    const copyAllCode = () => {
      const allCode = `<html>
    <head>
      <title>${selectedHtml.value}</title>
      <style>${cssCode.value}</style>
    </head>
    <body>
      ${htmlCode.value}
    </body>
  </html>`;
      navigator.clipboard.writeText(allCode).then(() => {
        alert("All code copied to clipboard!");
      });
    };

    const saveContent = async (type) => {
      if (!authStore.user) {
        console.warn("Unauthorized user attempted to save content.");
        return;
      }

      let docId = "";
      let userPath = `users/${authStore.user.uid}/snippets`; // Default path to user's snippets

      try {
        if (type === "CSS") {
          docId = selectedCssDocId.value;
        } else if (type === "HTML") {
          docId = selectedHtmlDocId.value;
        }

        // 🔥 Check if the snippet is public
        const selectedSnippet = dbStore.snippets.find(snippet => snippet.id === docId);
        if (authStore.admin && selectedSnippet?.isPublic) {
          userPath = "users/public/snippets"; // Admin can save to public snippets
        }

        const docRef = doc(db, userPath, docId);
        const updateData = { content: type === "CSS" ? cssCode.value : htmlCode.value };

        if (type === "HTML") {
          updateData.css = selectedCssDocId.value; // Include associated CSS reference
        }

        await updateDoc(docRef, updateData);

        if (type === "CSS") {
          originalCss.value = cssCode.value;
        } else if (type === "HTML") {
          originalHtml.value = htmlCode.value;
        }

        console.log(`Updated ${type} document '${docId}' in '${userPath}'.`);
      } catch (error) {
        console.error(`Error updating ${type} document '${docId}':`, error);
      }
    };

    const canEditSnippet = (type) => {
      if (!authStore.user) return false; // Not logged in, no edits allowed

      let docId = type === "CSS" ? selectedCssDocId.value : selectedHtmlDocId.value;
      const snippet = dbStore.snippets.find(snippet => snippet.id === docId);

      if (!snippet) return false; // No snippet found

      // Allow if the user owns the snippet OR it's public and the user is an admin
      return snippet.isPublic ? authStore.admin : snippet.owner === authStore.user.uid;
    };

    const updateContent = (type) => {
      const selectedOption = type === "CSS" ? selectedCss.value : selectedHtml.value;

      // Find the selected snippet in the store
      const selectedSnippet = dbStore.snippets.find(
        (snippet) => snippet.type === type && snippet.name === selectedOption
      );

      if (!selectedSnippet) {
        console.warn(`No matching snippet found for ${type}: ${selectedOption}`);
        return;
      }

      console.log(`Matched document for ${type}:`, selectedSnippet.id);

      if (type === "CSS") {
        cssCode.value = selectedSnippet.content;
        selectedCssDocId.value = selectedSnippet.id; // Store the Firestore docID
        originalCss.value = selectedSnippet.content; // Sets Value to check for changes
      } else if (type === "HTML") {
        htmlCode.value = selectedSnippet.content;
        selectedHtmlDocId.value = selectedSnippet.id; // Store the Firestore docID
        originalHtml.value = selectedSnippet.content; // Sets Value to check for changes

        // Check if the HTML snippet has an associated CSS snippet
        if (selectedSnippet.css) {
          console.log("CSS docID found in HTML snippet:", selectedSnippet.css);

          // Find the associated CSS snippet in the store
          const cssSnippet = dbStore.snippets.find(
            (snippet) => snippet.id === selectedSnippet.css
          );

          if (cssSnippet) {
            console.log("Fetched associated CSS document:", cssSnippet);

            // Update the selectedCss and fetch its content
            selectedCss.value = cssSnippet.name;
            updateContent("CSS"); // Recursively call updateContent for CSS
          } else {
            console.warn(`No CSS document found with docID: ${selectedSnippet.css}`);
          }
        }
      }
    };

    return {
      user,
      username,
      cssCode,
      htmlCode,
      cssOptions,
      htmlOptions,
      selectedCss,
      selectedHtml,
      selectedCssDocId,
      selectedHtmlDocId,
      showNewPopup,
      showEditPopup,
      showRunPopup,
      popupType,
      originalCss,
      originalHtml,
      htmlIsChanged,
      cssIsChanged,
      loadData,
      copyCss,
      copyHtml,
      copyAllCode,
      saveContent,
      updateContent,
      canEditSnippet,
      showEditDetailsPopup,
      authStore
    };
  },
};
</script>

<style>
body {
  margin: 0;
  font-family: Consolas, "Courier New", monospace;
  background-color: #1e1e1e;
  color: #d4d4d4;
}

#app {
  padding: 10px;
}

main p {
  margin-bottom: 5px;
  margin-top:10px;
}

main select {
  padding: 5px;
  background-color: #333;
  color: #d4d4d4;
  border: 1px solid #555;
  border-radius: 4px;
  margin-top: 0px;
}

button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

h3 {
  color: #007acc;
  margin: 8px 0 0 0;
}
</style>